/*
 * <recommendations-slider> — fetches product recommendations via the Shopify
 * Product Recommendations API and, once the markup is swapped in, boots the
 * Swiper carousel. A normal <script> inside fetched HTML never executes, so the
 * slider has to be initialised here, after the innerHTML swap.
 */
class RecommendationsSlider extends HTMLElement {
  connectedCallback() {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations() {
    const productId = this.dataset.productId;
    if (!productId) return;

    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('recommendations-slider');

        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.initSlider();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initSlider() {
    const el = this.querySelector('.swiper');
    if (!el) return;

    const sectionId = this.dataset.sectionId;

    const build = () => {
      if (typeof Swiper === 'undefined') return false;
      if (el.dataset.swiperInit === 'true') return true;
      el.dataset.swiperInit = 'true';

      const swiper = new Swiper(el, {
        slidesPerView: 1.2,
        spaceBetween: 16,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        breakpoints: {
          750: { slidesPerView: 2.2, spaceBetween: 16 },
          990: { slidesPerView: 3.2, spaceBetween: 16 },
          1600: { slidesPerView: 4.2, spaceBetween: 16 },
        },
        navigation: {
          prevEl: `#FeaturedRecommendationsPrev-${sectionId}`,
          nextEl: `#FeaturedRecommendationsNext-${sectionId}`,
        },
        pagination: {
          el: `#FeaturedRecommendationsProgress-${sectionId}`,
          type: 'progressbar',
        },
      });

      // Recalculate once images/fonts settle so slide widths are correct.
      window.addEventListener('load', function () { swiper.update(); });

      return true;
    };

    if (!build()) {
      // Swiper bundle may still be loading.
      let attempts = 0;
      const timer = setInterval(function () {
        attempts++;
        if (build() || attempts > 50) clearInterval(timer);
      }, 100);
    }
  }
}

if (!customElements.get('recommendations-slider')) {
  customElements.define('recommendations-slider', RecommendationsSlider);
}
